/**
 * Reads one dropped glasses product folder and decides — before anything is
 * uploaded — whether it is unambiguously importable.
 *
 * Expected shape, and the only shape accepted:
 *
 *   Aviator Glasses 232/          <- one root product folder
 *     orange/                     <- one to four colour folders
 *       232915-eyeglasses-front-view.webp
 *       232915-eyeglasses-side-view.webp
 *       232915-eyeglasses-angle-view.webp
 *
 * The role tokens are not a convention invented here. They are the tokens the
 * real photography library already uses: every file in the source library is
 * named "<code>-eyeglasses-<role>-view.<ext>" with role in front / side / angle.
 *
 * The parser refuses rather than guesses. A folder that is merely probably
 * right still becomes a real product with real photographs on a real storefront,
 * so every uncertainty is surfaced as a problem the administrator resolves,
 * and nothing is uploaded until the whole folder is clean.
 */

import {
  matchColorFolderName,
  normalizeColorFolderName,
  type ColorMatch,
  type PaletteColor,
} from "../catalog/colors";

/**
 * The swatch a colour starts with when the shop has never filed one under this
 * name. Same value the custom-colour form already defaults to (AddNewColor's
 * `useState("#808080")`) — deliberately not a second fallback.
 */
const LOCAL_SWATCH_FALLBACK = "#808080";

/**
 * The colour a folder names, resolved for this product.
 *
 * A folder called "red and black" is a perfectly good product colour: it is
 * what the photographer filed the photographs under and what a shopper will be
 * shown. It simply is not in the shop's palette, and it does not need to be —
 * the palette exists to *supply a swatch* for names it already knows, not to
 * grant permission for the rest.
 *
 * Treating an unknown name as an unanswered question is what stopped Add
 * Product: the card read "Needs attention", offered Add New Colour, and held
 * the next section shut until the operator picked something from a list that
 * did not contain the answer. So an unmatched name resolves here, locally, to
 * the folder's own name with the shared fallback swatch. Nothing is written to
 * the shop's palette — this colour belongs to this variant, and its swatch
 * stays editable afterwards in Edit → Colors & stock.
 *
 * An ambiguous name is left alone: two approved shades really do share that
 * name, and choosing between two candidates is a genuine question with a short
 * answer.
 */
function resolveColorName(
  folderName: string,
  palette: readonly PaletteColor[],
): ColorMatch {
  const match = matchColorFolderName(folderName, palette);
  if (match.kind !== "unknown") return match;
  return {
    kind: "matched",
    detectedName: match.detectedName,
    color: {
      name: folderName.trim(),
      color_key: normalizeColorFolderName(folderName),
      swatch_hex: LOCAL_SWATCH_FALLBACK,
      is_builtin: false,
    },
  };
}

/** One file as the browser handed it to us, with its path inside the drop. */
export interface PickedFile {
  /** Path relative to (and including) the dropped root folder. */
  relativePath: string;
  name: string;
  size: number;
  file: File;
}

export const MAX_COLORS = 4;

/** Formats the stored image contract accepts (ImageFormat in the product contract). */
const SUPPORTED_EXTENSIONS = ["webp", "avif", "png", "jpg", "jpeg"] as const;

export type SourceRole = "main" | "side" | "angle";

export const SOURCE_ROLES: readonly SourceRole[] = ["main", "side", "angle"];

export const ROLE_LABELS: Record<SourceRole, string> = {
  main: "Main",
  side: "Side",
  angle: "Angle",
};

/**
 * Filename tokens that name a role.
 *
 * "main" and "front" are the same view under two names; so are "angle" and
 * "diagonal". Matching is by whole token, not substring, so a product code that
 * happens to contain the letters "side" cannot masquerade as a side view.
 */
const ROLE_TOKENS: Record<SourceRole, readonly string[]> = {
  main: ["front", "main"],
  side: ["side"],
  angle: ["angle", "diagonal"],
};

export interface ParsedSource {
  role: SourceRole;
  file: PickedFile;
}

/** Why the parser would not place a file on its own. */
export type UnplacedReason = "ambiguous" | "unrecognized" | "duplicate";

/**
 * A file the parser refused to place, kept so the administrator can place it.
 *
 * The parser will not guess between two candidate views, and it will not pick
 * one of two files claiming the same view. Both answers exist — the operator
 * knows which photograph is which — so the file is carried through with the
 * reason rather than dropped, and the review offers it back.
 */
export interface UnplacedFile {
  file: PickedFile;
  reason: UnplacedReason;
  /** For "duplicate", the role both files claimed. */
  role?: SourceRole;
}

export interface ParsedColor {
  /** The colour folder name exactly as it appeared. */
  folderName: string;
  /** Palette match, or an unknown that needs one click to resolve. */
  color: ColorMatch;
  /** Exactly one file per role, in main / side / angle order. */
  sources: ParsedSource[];
  /** Files awaiting an explicit view from the administrator. Empty when the
   *  folder was unambiguous. */
  unplaced: UnplacedFile[];
}

export interface ParsedGlassesFolder {
  rootName: string;
  colors: ParsedColor[];
}

export type FolderProblemCode =
  | "no_files"
  | "multiple_roots"
  | "no_color_folders"
  | "too_many_colors"
  | "unexpected_nesting"
  | "file_at_root"
  | "unsupported_file"
  | "empty_file"
  | "missing_role"
  | "duplicate_role"
  | "ambiguous_role"
  | "unrecognized_file";

export interface FolderProblem {
  code: FolderProblemCode;
  /** Plain-language sentence shown directly to the administrator. */
  message: string;
  /** The colour folder this concerns, when it concerns one. */
  colorFolder?: string;
}

/**
 * Which faults the administrator can answer here, and which need the folder
 * changed on disk.
 *
 * A view the parser would not guess at is a question with an answer the
 * operator already has, so it is asked. A folder holding two products, or a
 * file the pipeline cannot read, is not a question — it is a folder that has to
 * be fixed and chosen again.
 */
const CORRECTABLE: ReadonlySet<FolderProblemCode> = new Set<FolderProblemCode>([
  "missing_role",
  "duplicate_role",
  "ambiguous_role",
  "unrecognized_file",
]);

export function isCorrectable(problem: FolderProblem): boolean {
  return CORRECTABLE.has(problem.code);
}

export type GlassesFolderParse =
  | { ok: true; product: ParsedGlassesFolder }
  | {
      ok: false;
      rootName: string | null;
      problems: FolderProblem[];
      /** What was read per colour before the faults stopped it, so the review
       *  can show each colour and take the answers. Absent when the selection
       *  never got as far as having colour folders at all. */
      colors?: ParsedColor[];
    };

/** Finder metadata and other dotfiles: present in every real folder, never content. */
function isIgnorable(segment: string): boolean {
  return segment.startsWith(".") || segment === "Icon\r" || segment === "Thumbs.db";
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

function isSupported(name: string): boolean {
  return (SUPPORTED_EXTENSIONS as readonly string[]).includes(extensionOf(name));
}

/** Split a basename into whole words so role tokens match exactly. */
function tokenize(name: string): string[] {
  const dot = name.lastIndexOf(".");
  const stem = dot === -1 ? name : name.slice(0, dot);
  return stem.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
}

/**
 * Which role a filename names.
 *
 * Returns "ambiguous" when a name claims two roles at once — that file could be
 * either view, and picking one would be a coin flip on which photograph a
 * shopper sees first.
 */
export function roleFromFilename(name: string): SourceRole | "ambiguous" | null {
  const tokens = new Set(tokenize(name));
  const hits = SOURCE_ROLES.filter((role) =>
    ROLE_TOKENS[role].some((token) => tokens.has(token)),
  );
  if (hits.length === 1) return hits[0]!;
  if (hits.length > 1) return "ambiguous";
  return null;
}

function splitPath(relativePath: string): string[] {
  return relativePath.split("/").filter(Boolean);
}

/**
 * Parse one dropped folder.
 *
 * Every problem found is reported, not just the first: an administrator fixing
 * a folder should see everything wrong with it in one pass rather than
 * rediscovering the next fault after each correction.
 */
export function parseGlassesFolder(
  files: PickedFile[],
  /** The shop's palette, for naming the detected colours. An empty palette is
   *  valid — every colour then reports as unknown and is added deliberately. */
  palette: readonly PaletteColor[] = [],
): GlassesFolderParse {
  const problems: FolderProblem[] = [];

  const visible = files.filter(
    (f) => !splitPath(f.relativePath).some((segment) => isIgnorable(segment)),
  );

  if (visible.length === 0) {
    return {
      ok: false,
      rootName: null,
      problems: [
        {
          code: "no_files",
          message: "That folder has no images in it. Choose the product folder itself.",
        },
      ],
    };
  }

  const roots = new Set(visible.map((f) => splitPath(f.relativePath)[0]!));
  if (roots.size > 1) {
    return {
      ok: false,
      rootName: null,
      problems: [
        {
          code: "multiple_roots",
          message:
            "Drop one product folder at a time. This selection contains " +
            `${roots.size} separate folders (${[...roots].sort().join(", ")}).`,
        },
      ],
    };
  }

  const rootName = [...roots][0]!;

  // Group by colour folder, collecting structural faults as we go.
  const byColor = new Map<string, PickedFile[]>();
  for (const picked of visible) {
    const segments = splitPath(picked.relativePath);

    if (segments.length === 2) {
      problems.push({
        code: "file_at_root",
        message:
          `"${picked.name}" sits directly in the product folder. Every photograph ` +
          "belongs inside a colour folder.",
      });
      continue;
    }
    if (segments.length > 3) {
      problems.push({
        code: "unexpected_nesting",
        message:
          `"${picked.name}" is nested too deeply (${segments.slice(1, -1).join(" / ")}). ` +
          "A colour folder holds its three photographs directly.",
        colorFolder: segments[1],
      });
      continue;
    }

    const colorFolder = segments[1]!;
    const bucket = byColor.get(colorFolder);
    if (bucket) bucket.push(picked);
    else byColor.set(colorFolder, [picked]);
  }

  const colorNames = [...byColor.keys()].sort((a, b) => a.localeCompare(b));

  if (colorNames.length === 0) {
    problems.push({
      code: "no_color_folders",
      message:
        `"${rootName}" has no colour folders. Each colour needs its own folder ` +
        "holding a main, a side and an angle photograph.",
    });
    return { ok: false, rootName, problems };
  }

  if (colorNames.length > MAX_COLORS) {
    problems.push({
      code: "too_many_colors",
      message:
        `"${rootName}" has ${colorNames.length} colour folders. A product supports ` +
        `up to ${MAX_COLORS}.`,
    });
  }

  const colors: ParsedColor[] = [];

  for (const folderName of colorNames) {
    const entries = byColor.get(folderName)!;
    const assigned = new Map<SourceRole, PickedFile[]>();
    // Files the parser will not place on its own. Carried through rather than
    // dropped: the operator can say which view each one is.
    const unplaced: UnplacedFile[] = [];

    for (const picked of entries) {
      if (!isSupported(picked.name)) {
        problems.push({
          code: "unsupported_file",
          message:
            `"${picked.name}" is not an image the pipeline can read. Use ` +
            `${SUPPORTED_EXTENSIONS.join(", ")}.`,
          colorFolder: folderName,
        });
        continue;
      }
      if (picked.size === 0) {
        problems.push({
          code: "empty_file",
          message: `"${picked.name}" is empty (0 bytes). Re-export it and try again.`,
          colorFolder: folderName,
        });
        continue;
      }

      const role = roleFromFilename(picked.name);
      if (role === "ambiguous") {
        problems.push({
          code: "ambiguous_role",
          message:
            `"${picked.name}" names more than one view, so which photograph it is ` +
            "cannot be determined. Assign its view below.",
          colorFolder: folderName,
        });
        unplaced.push({ file: picked, reason: "ambiguous" });
        continue;
      }
      if (role === null) {
        problems.push({
          code: "unrecognized_file",
          message:
            `"${picked.name}" does not say which view it is. Assign its view below, ` +
            "or remove it from the folder.",
          colorFolder: folderName,
        });
        unplaced.push({ file: picked, reason: "unrecognized" });
        continue;
      }

      const bucket = assigned.get(role);
      if (bucket) bucket.push(picked);
      else assigned.set(role, [picked]);
    }

    for (const role of SOURCE_ROLES) {
      const found = assigned.get(role) ?? [];
      if (found.length === 0) {
        problems.push({
          code: "missing_role",
          message: `"${folderName}" has no ${ROLE_LABELS[role]} photograph.`,
          colorFolder: folderName,
        });
      } else if (found.length > 1) {
        problems.push({
          code: "duplicate_role",
          message:
            `"${folderName}" has ${found.length} ${ROLE_LABELS[role]} photographs ` +
            `(${found.map((f) => f.name).join(", ")}). Each colour needs exactly one.`,
          colorFolder: folderName,
        });
        // Neither file is picked for the operator. Both are offered back so
        // they can say which is which, or set one aside.
        for (const file of found) unplaced.push({ file, reason: "duplicate", role });
      }
    }

    colors.push({
      folderName,
      color: resolveColorName(folderName, palette),
      sources: SOURCE_ROLES.flatMap((role) => {
        const found = assigned.get(role) ?? [];
        return found.length === 1 ? [{ role, file: found[0]! }] : [];
      }),
      unplaced,
    });
  }

  if (problems.length > 0) return { ok: false, rootName, problems, colors };
  return { ok: true, product: { rootName, colors } };
}

/* -------------------------------------------------------------------------- */
/* Correcting what the parser would not guess                                  */
/* -------------------------------------------------------------------------- */

/** What the administrator said one file is. "unused" leaves it out entirely. */
export type RoleChoice = SourceRole | "unused";

/** Keyed by relativePath, which is unique across the whole dropped folder. */
export type RoleOverrides = Record<string, RoleChoice>;

/** The problem a file still carries while its view is unanswered. */
const UNPLACED_PROBLEM: Record<UnplacedReason, FolderProblemCode> = {
  ambiguous: "ambiguous_role",
  unrecognized: "unrecognized_file",
  duplicate: "duplicate_role",
};

export interface ResolvedGlassesFolder {
  /** The folder as it now stands, or null while anything is still unanswered. */
  product: ParsedGlassesFolder | null;
  /** What is still wrong, after the administrator's answers are applied. */
  problems: FolderProblem[];
  /** True when nothing is left that the administrator could answer here — the
   *  folder itself has to change. */
  blocked: boolean;
}

/**
 * Apply the administrator's view assignments and re-judge the folder.
 *
 * Deliberately a pure function over the parse rather than a second parser: the
 * rules that decide whether a colour is importable — exactly one Main, one Side
 * and one Angle — must not exist in two places, so they are re-run here over
 * the same shape and nothing re-reads a filename.
 *
 * Structural faults are not answerable here and are carried through untouched.
 * A folder holding two products cannot be fixed by naming a view.
 */
export function resolveGlassesFolder(
  parse: GlassesFolderParse,
  overrides: RoleOverrides,
): ResolvedGlassesFolder {
  if (parse.ok) return { product: parse.product, problems: [], blocked: false };

  const structural = parse.problems.filter((problem) => !isCorrectable(problem));
  const colors = parse.colors;

  // Nothing to correct: the selection never became colour folders.
  if (!colors || colors.length === 0) {
    return { product: null, problems: parse.problems, blocked: true };
  }

  const problems: FolderProblem[] = [...structural];
  const resolved: ParsedColor[] = [];

  for (const color of colors) {
    // Start from what the parser placed, minus anything the operator moved,
    // then add every file they gave a view to.
    const byRole = new Map<SourceRole, PickedFile[]>();
    const add = (role: SourceRole, file: PickedFile) => {
      const bucket = byRole.get(role);
      if (bucket) bucket.push(file);
      else byRole.set(role, [file]);
    };

    for (const source of color.sources) {
      const choice = overrides[source.file.relativePath];
      if (choice === "unused") continue;
      add(choice ?? source.role, source.file);
    }
    for (const item of color.unplaced) {
      const choice = overrides[item.file.relativePath];
      if (choice === undefined || choice === "unused") continue;
      add(choice, item.file);
    }

    const stillUnplaced = color.unplaced.filter(
      (item) => overrides[item.file.relativePath] === undefined,
    );

    for (const item of stillUnplaced) {
      problems.push({
        code: UNPLACED_PROBLEM[item.reason],
        message: `Say which view "${item.file.name}" is.`,
        colorFolder: color.folderName,
      });
    }

    for (const role of SOURCE_ROLES) {
      const found = byRole.get(role) ?? [];
      if (found.length === 0) {
        problems.push({
          code: "missing_role",
          message: `"${color.folderName}" has no ${ROLE_LABELS[role]} photograph.`,
          colorFolder: color.folderName,
        });
      } else if (found.length > 1) {
        problems.push({
          code: "duplicate_role",
          message:
            `"${color.folderName}" has ${found.length} ${ROLE_LABELS[role]} ` +
            `photographs (${found.map((f) => f.name).join(", ")}). ` +
            "Each colour needs exactly one.",
          colorFolder: color.folderName,
        });
      }
    }

    resolved.push({
      ...color,
      sources: SOURCE_ROLES.flatMap((role) => {
        const found = byRole.get(role) ?? [];
        return found.length === 1 ? [{ role, file: found[0]! }] : [];
      }),
      unplaced: stillUnplaced,
    });
  }

  if (problems.length > 0) {
    return { product: null, problems, blocked: structural.length > 0 };
  }
  return {
    product: { rootName: parse.rootName ?? "", colors: resolved },
    problems: [],
    blocked: false,
  };
}
